import React, { useState, useEffect } from 'react';
import { Settings, Lock, Trash2, Eye, EyeOff, AlertTriangle, ChevronRight, Loader, CheckCircle2, AlertCircle, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePortfolio } from '../hooks/usePortfolio';
import authService from '../services/auth.service';

export default function SettingsPage() {
  const { logout } = useAuth();
  const { settings, loading: settingsLoading, fetchSettings } = usePortfolio();
  
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showDeleteSection, setShowDeleteSection] = useState(false);
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [referralData, setReferralData] = useState({ count: 0, promoCode: null });
  const [referralLoading, setReferralLoading] = useState(true);

  const userEmail = authService.getEmail();
  const userId = authService.getUserId();

  const fetchReferralData = async () => {
    setReferralLoading(true);
    try {
      const data = await authService.getReferralCount(userId);
      setReferralData(data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setReferralLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchReferralData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (message) => {
    setErrorMessage(message);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  const handlePasswordUpdate = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Please fill in all password fields');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match!');
      return;
    }
    
    if (newPassword.length < 8) {
      showError('Password must be at least 8 characters long');
      return;
    }

    if (currentPassword === newPassword) {
      showError('New password must be different from current password');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.utajirihub.com/api'}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          email: userEmail,
          currentPassword: currentPassword,
          newPassword: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        showSuccess('Password updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
      } else {
        showError(data.message || 'Failed to update password');
      }
    } catch (error) {
      showError('Error updating password. Please try again.');
      console.error('Password update error:', error);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.toLowerCase() !== 'delete my account') {
      showError('Please type "DELETE MY ACCOUNT" exactly to confirm');
      return;
    }

    if (!deletePassword) {
      showError('Please enter your password to confirm account deletion');
      return;
    }

    setDeleteLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://api.utajirihub.com/api'}/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          email: userEmail,
          password: deletePassword
        })
      });

      if (response.ok) {
        showSuccess('Account deleted successfully. Logging out...');
        setTimeout(() => {
          logout();
          window.location.href = '/';
        }, 1500);
      } else {
        const data = await response.json();
        showError(data.error || data.message || 'Failed to delete account');
      }
    } catch (error) {
      showError('Error deleting account. Please try again.');
      console.error('Account deletion error:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getMemberSinceDate = () => {
    if (settings?.createdAt) {
      const date = new Date(settings.createdAt);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return 'Recently';
  };

  if (settingsLoading) {
    return (
      <div className="min-h-screen bg-[#0b141a] flex items-center justify-center">
        <div className="text-gray-400 text-base">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b141a]">
      <main className="max-w-2xl mx-auto pb-20">

        {/* Header */}
        <div className="bg-[#1f2c34] px-4 py-5 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#00a884]/10 flex items-center justify-center">
              <Settings className="w-6 h-6 text-[#00a884]" />
            </div>
            <div>
              <h1 className="text-white text-xl font-medium">Settings</h1>
              <p className="text-[#8696a0] text-sm">Manage your account</p>
            </div>
          </div>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mx-4 mt-2 bg-[#00a884]/10 border border-[#00a884]/20 rounded-lg p-3 flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#00a884] flex-shrink-0 mt-0.5" />
            <p className="text-[#00a884] text-sm flex-1">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mx-4 mt-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm flex-1">{errorMessage}</p>
          </div>
        )}

        <div className="px-4 pt-4">
          {/* Account Info */}
          <div className="mb-2">
            <p className="text-[#8696a0] text-xs uppercase tracking-wide px-4 mb-2">Account Information</p>
            <div className="bg-[#1f2c34] rounded-lg overflow-hidden">
              <div className="px-4 py-3 flex justify-between items-center border-b border-[#2a3942]">
                <span className="text-[#8696a0] text-sm">Email</span>
                <span className="text-white text-sm">{userEmail || 'Not available'}</span>
              </div>
              <div className="px-4 py-3 flex justify-between items-center border-b border-[#2a3942]">
                <span className="text-[#8696a0] text-sm">Currency</span>
                <span className="text-[#00a884] text-sm">{settings?.currency || 'KES'}</span>
              </div>
              <div className="px-4 py-3 flex justify-between items-center border-b border-[#2a3942]">
                <span className="text-[#8696a0] text-sm">Timezone</span>
                <span className="text-white text-sm">{settings?.timezone || 'Africa/Nairobi'}</span>
              </div>
              <div className="px-4 py-3 flex justify-between items-center border-b border-[#2a3942]">
                <span className="text-[#8696a0] text-sm">Member Since</span>
                <span className="text-white text-sm">{getMemberSinceDate()}</span>
              </div>
              
              {/* My Referrals */}
              <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#00a884]" />
                    <span className="text-[#8696a0] text-sm">My Referrals</span>
                  </div>
                  {referralLoading ? (
                    <Loader className="w-4 h-4 text-[#8696a0] animate-spin" />
                  ) : (
                    <span className="text-[#00a884] text-lg font-semibold">{referralData.count}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Security */}
          <div className="mt-6 mb-2">
            <p className="text-[#8696a0] text-xs uppercase tracking-wide px-4 mb-2">Security</p>
            <div className="bg-[#1f2c34] rounded-lg overflow-hidden">
              <button
                onClick={() => setShowPasswordSection(!showPasswordSection)}
                className="w-full px-4 py-3 flex items-center justify-between active:bg-[#2a3942] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Lock className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-normal">Change Password</p>
                    <p className="text-[#8696a0] text-xs">Update your password</p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 text-[#8696a0] transition-transform ${showPasswordSection ? 'rotate-90' : ''}`} />
              </button>

              {showPasswordSection && (
                <div className="px-4 pb-4 border-t border-[#2a3942] pt-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[#8696a0] text-sm mb-2">Current Password</label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="Enter current password"
                          className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-3 pr-10 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#2a3942]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8696a0]"
                        >
                          {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#8696a0] text-sm mb-2">New Password</label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-3 pr-10 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#2a3942]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8696a0]"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#8696a0] text-sm mb-2">Confirm Password</label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Re-enter new password"
                          className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-3 pr-10 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 border border-[#2a3942]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8696a0]"
                        >
                          {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => {
                          setShowPasswordSection(false);
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                          setErrorMessage('');
                        }}
                        disabled={passwordLoading}
                        className="flex-1 bg-[#2a3942] text-white rounded-lg px-4 py-3 font-medium active:bg-[#374047] transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handlePasswordUpdate}
                        disabled={passwordLoading}
                        className="flex-1 bg-blue-500 text-white rounded-lg px-4 py-3 font-medium active:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {passwordLoading && <Loader className="w-4 h-4 animate-spin" />}
                        {passwordLoading ? 'Updating...' : 'Update'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Danger Zone */}
          <div className="mt-6">
            <p className="text-red-400 text-xs uppercase tracking-wide px-4 mb-2">Danger Zone</p>
            <div className="bg-[#1f2c34] rounded-lg overflow-hidden border border-red-500/20">
              <button
                onClick={() => setShowDeleteSection(!showDeleteSection)}
                className="w-full px-4 py-3 flex items-center justify-between active:bg-[#2a3942] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-normal">Delete Account</p>
                    <p className="text-[#8696a0] text-xs">Permanently delete all data</p>
                  </div>
                </div>
                <ChevronRight className={`w-5 h-5 text-[#8696a0] transition-transform ${showDeleteSection ? 'rotate-90' : ''}`} />
              </button>

              {showDeleteSection && (
                <div className="px-4 pb-4 border-t border-[#2a3942] pt-4">
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-red-400 text-sm font-medium mb-1">Warning: Cannot be undone</p>
                        <p className="text-[#8696a0] text-xs mb-2">This will permanently delete:</p>
                        <ul className="text-[#8696a0] text-xs space-y-1 ml-4 list-disc">
                          <li>All investments</li>
                          <li>All loans</li>
                          <li>Transaction history</li>
                          <li>Account settings</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-[#8696a0] text-sm mb-2">
                        Type <span className="text-red-400 font-bold">"DELETE MY ACCOUNT"</span>
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="DELETE MY ACCOUNT"
                        className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 border border-[#2a3942]"
                      />
                    </div>

                    <div>
                      <label className="block text-[#8696a0] text-sm mb-2">Password</label>
                      <div className="relative">
                        <input
                          type={showDeletePassword ? 'text' : 'password'}
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          placeholder="Enter your password"
                          className="w-full bg-[#2a3942] text-white placeholder-[#8696a0] rounded-lg px-3 pr-10 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 border border-[#2a3942]"
                        />
                        <button
                          type="button"
                          onClick={() => setShowDeletePassword(!showDeletePassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-[#8696a0]"
                        >
                          {showDeletePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={() => {
                          setShowDeleteSection(false);
                          setDeleteConfirmation('');
                          setDeletePassword('');
                          setErrorMessage('');
                        }}
                        disabled={deleteLoading}
                        className="flex-1 bg-[#2a3942] text-white rounded-lg px-4 py-3 font-medium active:bg-[#374047] transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmation.toLowerCase() !== 'delete my account' || !deletePassword || deleteLoading}
                        className="flex-1 bg-red-500 text-white rounded-lg px-4 py-3 font-medium active:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {deleteLoading && <Loader className="w-4 h-4 animate-spin" />}
                        {deleteLoading ? 'Deleting...' : 'Delete Account'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}